import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { RootState } from '../store/store';
import {
  addVoiceUserMessage,
  addVoiceAssistantMessage,
  updateLastAssistantMessage,
  updateLastAssistantMessageWithFunctionCall,
  finalizeVoiceUserMessage,
  finalizeVoiceAssistantMessage,
  cancelTranscribingMessages,
} from '../store/chatModeSlice';
import {
  setVoiceModeActive,
  setVoiceModeListening,
  setVoiceModePaused,
  setVoiceModeRestarting,
  setShowInactivityWarning,
  setInactivityCountdown,
  setDisconnectedByInactivity,
  setShowNoAudioDialog,
} from '../store/voiceModeSlice';
import { voiceModeService } from '../utils/voiceMode.service';
import { checkMicrophonePermission } from '../utils/microphonePermission';
import logger from '../utils/logger';

export interface UseVoiceModeHandlersOptions {
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  chatPage?: boolean;
}

export function useVoiceModeHandlers({ onNavigateToChat, chatPage }: UseVoiceModeHandlersOptions) {
  const dispatch = useDispatch();
  const { instance: msalInstance } = useMsal();
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const {
    isActive: isVoiceModeActive,
    isPaused: isVoiceModePaused,
    disconnectedByInactivity,
  } = voiceModeState;

  // ─── Local state ────────────────────────────────────────────────────────────
  const [voiceModeLoading, setVoiceModeLoading] = React.useState(false);
  const [audioLevel, setAudioLevel] = React.useState(0);
  const [frequencyData, setFrequencyData] = React.useState<Uint8Array | undefined>(undefined);
  const [showMicPermissionDialog, setShowMicPermissionDialog] = React.useState(false);
  const [showVoiceModeError, setShowVoiceModeError] = React.useState(false);
  const [voiceModeErrorMessage, setVoiceModeErrorMessage] = React.useState('');
  const [showMicSelector, setShowMicSelector] = React.useState(false);
  const [selectedMicDeviceId, setSelectedMicDeviceId] = React.useState<string | undefined>(() => {
    try {
      return localStorage.getItem('selectedMicrophoneId') || undefined;
    } catch {
      return undefined;
    }
  });

  // ─── Refs ────────────────────────────────────────────────────────────────────
  // Track if we've created an assistant message for the current turn
  const hasCreatedAssistantMessageRef = React.useRef(false);
  // Track if a voice user message is currently active
  const hasActiveVoiceUserMessageRef = React.useRef(false);
  // Track if assistant response is still streaming
  const assistantResponsePendingRef = React.useRef(false);
  // Track if voice session actually connected successfully
  const voiceSessionStartedRef = React.useRef(false);
  // Mirror of Redux isVoiceModeActive for use inside async callbacks
  const isVoiceModeActiveRef = React.useRef(isVoiceModeActive);
  // Set true while cancelling initialization to suppress stale callbacks
  const voiceModeCancelledRef = React.useRef(false);
  // Stable callback refs to avoid stale closures in audio worklet callbacks
  const audioLevelCallbackRef = React.useRef<((level: number) => void) | undefined>(undefined);
  const frequencyDataCallbackRef = React.useRef<((data: Uint8Array) => void) | undefined>(undefined);
  // Track previous voice mode active state to detect external shutdowns
  const prevVoiceModeActiveRef = React.useRef(isVoiceModeActive);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  // Keep isVoiceModeActiveRef in sync with Redux so async callbacks never read stale state
  React.useEffect(() => {
    isVoiceModeActiveRef.current = isVoiceModeActive;
  }, [isVoiceModeActive]);

  // Update stable callback refs on every render (no deps — intentional pattern)
  React.useEffect(() => {
    audioLevelCallbackRef.current = (level: number) => {
      if (!voiceModeCancelledRef.current) setAudioLevel(level);
    };
    frequencyDataCallbackRef.current = (data: Uint8Array) => {
      if (!voiceModeCancelledRef.current) setFrequencyData(data);
    };
  });

  // Re-register audio callbacks when the hook mounts during an already-active voice session
  // (e.g. user navigates to a new page while voice mode is running)
  React.useEffect(() => {
    if (isVoiceModeActive && voiceModeService.isConnected()) {
      logger.log('[useVoiceModeHandlers] Mounted during active voice mode – re-registering audio callbacks');
      voiceModeService.updateAudioCallbacks({
        onAudioLevel: (level: number) => {
          if (!voiceModeCancelledRef.current) setAudioLevel(level);
        },
        onFrequencyData: (data: Uint8Array) => {
          if (!voiceModeCancelledRef.current) setFrequencyData(data);
        },
        onAssistantReady: () => {
          if (voiceModeCancelledRef.current) return;
          dispatch(setVoiceModeListening(true));
          voiceSessionStartedRef.current = true;
        },
      });

      // If already listening (connection fully established), reflect that immediately
      if (voiceModeService.isListening()) {
        dispatch(setVoiceModeListening(true));
        voiceSessionStartedRef.current = true;
      }
    }
  }, [isVoiceModeActive, dispatch]);

  // Reset internal voice state when voice mode is turned off externally (e.g. tab switch)
  React.useEffect(() => {
    if (prevVoiceModeActiveRef.current === true && isVoiceModeActive === false) {
      voiceModeCancelledRef.current = false;
      hasCreatedAssistantMessageRef.current = false;
      hasActiveVoiceUserMessageRef.current = false;
      assistantResponsePendingRef.current = false;
      setAudioLevel(0);
    }
    prevVoiceModeActiveRef.current = isVoiceModeActive;
  }, [isVoiceModeActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Core handler: start a new voice session ─────────────────────────────────
  const startVoiceMode = async () => {
    // Set voice mode state IMMEDIATELY for instant UI transition
    dispatch(setVoiceModeActive(true));
    setVoiceModeLoading(true);

    // Clear any previous inactivity flags
    dispatch(setShowInactivityWarning(false));
    dispatch(setInactivityCountdown(0));
    dispatch(setDisconnectedByInactivity(false));

    // Reset cancellation flag
    voiceModeCancelledRef.current = false;
    hasCreatedAssistantMessageRef.current = false;
    hasActiveVoiceUserMessageRef.current = false;
    assistantResponsePendingRef.current = false;

    // Check microphone permission (async, but UI already transitioned)
    const permissionState = await checkMicrophonePermission();
    if (permissionState === 'denied') {
      // Revert state if permission denied
      dispatch(setVoiceModeActive(false));
      setVoiceModeLoading(false);
      setShowMicPermissionDialog(true);
      return;
    }

    // Set MSAL instance for authentication
    voiceModeService.setMsalInstance(msalInstance);

    // Determine voice mode server URL
    const voiceServerUrl = process.env.REACT_APP_VOICE_MODE_URL || 'wss://ai-np-apim-main.mmm.com';

    try {
      await voiceModeService.connect(voiceServerUrl, {
        deviceId: selectedMicDeviceId,
        onStatusChange: (status) => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] Voice mode status:', status);
          if (status === 'error' || status === 'disconnected') {
            logger.error('[useVoiceModeHandlers] Voice mode disconnected/error – updating UI state to inactive');
            setVoiceModeLoading(false);
            dispatch(setVoiceModeActive(false));
            dispatch(setVoiceModeListening(false));
            hasCreatedAssistantMessageRef.current = false;
            hasActiveVoiceUserMessageRef.current = false;
            assistantResponsePendingRef.current = false;
          }
        },
        onConnected: () => {
          if (voiceModeCancelledRef.current) return;

          // Navigate to chat page immediately on connection (before assistant sends greeting)
          if (onNavigateToChat && !chatPage) {
            onNavigateToChat('', []);
          }

          setVoiceModeLoading(false);
          dispatch(setVoiceModeActive(true));
        },
        onAssistantReady: () => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] Enabling audio waveform visualization');
          dispatch(setVoiceModeListening(true));

          voiceSessionStartedRef.current = true;
          logger.log('[useVoiceModeHandlers] Voice session started successfully');

          assistantResponsePendingRef.current = false;
          hasActiveVoiceUserMessageRef.current = false;
        },
        onAudioLevel: (level) => {
          if (voiceModeCancelledRef.current) return;
          setAudioLevel(level);
        },
        onFrequencyData: (data: Uint8Array) => {
          if (!voiceModeCancelledRef.current) setFrequencyData(data);
        },
        onAudioPlayback: (_audioHex: string) => {
          if (voiceModeCancelledRef.current) return;
          logger.log('[useVoiceModeHandlers] Received audio playback chunk from assistant');
          assistantResponsePendingRef.current = true;
        },
        onAssistantResponseStart: () => {
          if (voiceModeCancelledRef.current) return;
          assistantResponsePendingRef.current = true;
          hasCreatedAssistantMessageRef.current = false;
        },
        onTextDelta: (text: string) => {
          if (voiceModeCancelledRef.current) return;
          logger.log('[useVoiceModeHandlers] Received text delta:', text);

          if (!hasCreatedAssistantMessageRef.current) {
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }
          assistantResponsePendingRef.current = true;
          dispatch(updateLastAssistantMessage({ textDelta: text }));
        },
        onUserSpeechStart: () => {
          if (voiceModeCancelledRef.current) return;

          dispatch(setShowInactivityWarning(false));
          dispatch(setInactivityCountdown(0));

          if (!hasActiveVoiceUserMessageRef.current) {
            hasActiveVoiceUserMessageRef.current = true;
            dispatch(addVoiceUserMessage());
          }

          assistantResponsePendingRef.current = false;
          hasCreatedAssistantMessageRef.current = false;
        },
        onUserTranscriptStart: () => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] onUserTranscriptStart called');
          hasActiveVoiceUserMessageRef.current = true;
          dispatch(addVoiceUserMessage());
        },
        onUserTranscriptDelta: (_text: string) => {
          if (voiceModeCancelledRef.current) return;

          if (!hasActiveVoiceUserMessageRef.current) {
            hasActiveVoiceUserMessageRef.current = true;
            dispatch(addVoiceUserMessage());
          }
        },
        onFunctionCall: (message: string) => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] Function call:', message);

          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[useVoiceModeHandlers] Creating assistant message for function call');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }

          try {
            const parsed = JSON.parse(message);
            if (parsed.sources && Array.isArray(parsed.sources)) {
              logger.log('[useVoiceModeHandlers] Received sources:', parsed.sources);
              dispatch(updateLastAssistantMessageWithFunctionCall({ sources: parsed.sources }));
              return;
            }
          } catch {
            // Not JSON — treat as regular function call message
          }

          let functionType: 'web_search' | 'unknown' = 'unknown';
          if (message.toLowerCase().includes('search') && message.toLowerCase().includes('web')) {
            functionType = 'web_search';
          }

          dispatch(updateLastAssistantMessageWithFunctionCall({
            functionCall: { type: functionType, message, isActive: true },
          }));
        },
        onSearchSources: (data: { query: string; sources: Array<{ url: string; domain: string; title: string }> }) => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] Received search sources:', data);

          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[useVoiceModeHandlers] Creating assistant message for search sources');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }

          const formattedSources = data.sources.map(source => ({
            url: source.url,
            title: source.title || source.domain,
            snippet: source.domain,
          }));

          dispatch(updateLastAssistantMessageWithFunctionCall({ sources: formattedSources }));
        },
        onSearchProgress: (data: { message: string; query: string; elapsed: number }) => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] Search progress:', data);

          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[useVoiceModeHandlers] Creating assistant message for search progress');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }

          dispatch(updateLastAssistantMessageWithFunctionCall({
            functionCall: { type: 'web_search', message: data.message, isActive: true },
          }));
        },
        onTranscriptionComplete: (transcript: string, audioData?: string[], _messageId?: string) => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[useVoiceModeHandlers] User transcription complete:', transcript);
          dispatch(finalizeVoiceUserMessage({ transcript, audioData }));
          hasActiveVoiceUserMessageRef.current = false;
        },
        onAssistantResponseComplete: (audioData?: string[]) => {
          if (voiceModeCancelledRef.current) return;

          if (hasActiveVoiceUserMessageRef.current) {
            dispatch(finalizeVoiceUserMessage({ transcript: '' }));
            hasActiveVoiceUserMessageRef.current = false;
          }

          if (hasCreatedAssistantMessageRef.current) {
            dispatch(finalizeVoiceAssistantMessage({ audioData }));
          }

          hasCreatedAssistantMessageRef.current = false;
          assistantResponsePendingRef.current = false;
        },
        onError: (error) => {
          console.error('[useVoiceModeHandlers] Voice mode error:', error);

          if (voiceSessionStartedRef.current) {
            if (!hasCreatedAssistantMessageRef.current) {
              hasCreatedAssistantMessageRef.current = true;
              dispatch(addVoiceAssistantMessage());
            }

            const errorText = `\n\n⚠️ **Error**: ${error.message || 'An error occurred during voice mode.'}\n`;
            dispatch(updateLastAssistantMessage({ textDelta: errorText }));

            if (hasCreatedAssistantMessageRef.current) {
              dispatch(finalizeVoiceAssistantMessage({ audioData: undefined }));
              hasCreatedAssistantMessageRef.current = false;
            }
          } else {
            logger.log('[useVoiceModeHandlers] Session never started, not adding error to chat history');
          }

          setVoiceModeErrorMessage(
            error.message || 'Unable to connect to voice mode server. Please try again later.'
          );
          setShowVoiceModeError(true);

          setVoiceModeLoading(false);
          dispatch(setVoiceModeActive(false));
          dispatch(setVoiceModeListening(false));
          dispatch(setVoiceModePaused(false));
          dispatch(setVoiceModeRestarting(false));
          hasActiveVoiceUserMessageRef.current = false;
          voiceSessionStartedRef.current = false;
        },
        onNoAudioDetected: () => {
          logger.log('[useVoiceModeHandlers] onNoAudioDetected entered. voiceModeCancelledRef:', voiceModeCancelledRef.current);
          if (voiceModeCancelledRef.current) {
            logger.log('[useVoiceModeHandlers] onNoAudioDetected – SUPPRESSED because voiceModeCancelledRef is true');
            return;
          }

          logger.log('[useVoiceModeHandlers] onNoAudioDetected – setting showNoAudioDialog=true');
          console.warn('[useVoiceModeHandlers] No audio detected from microphone');
          dispatch(setShowNoAudioDialog(true));
        },
        onInactivityWarning: () => {
          if (voiceModeCancelledRef.current) return;
          logger.log('[useVoiceModeHandlers] Inactivity warning triggered');
          dispatch(setShowInactivityWarning(true));
        },
        onInactivityCountdown: (secondsRemaining: number) => {
          if (voiceModeCancelledRef.current) return;
          logger.log('[useVoiceModeHandlers] Inactivity countdown:', secondsRemaining);
          dispatch(setInactivityCountdown(secondsRemaining));
        },
        onInactivityTimeout: () => {
          logger.log('[useVoiceModeHandlers] Inactivity timeout – ending voice mode');

          if (!isVoiceModeActiveRef.current) {
            logger.log('[useVoiceModeHandlers] Inactivity timeout ignored – voice mode already inactive in Redux');
            return;
          }

          if (hasActiveVoiceUserMessageRef.current) {
            dispatch(finalizeVoiceUserMessage({ transcript: '' }));
            hasActiveVoiceUserMessageRef.current = false;
          }

          if (hasCreatedAssistantMessageRef.current) {
            dispatch(finalizeVoiceAssistantMessage({ audioData: undefined }));
            hasCreatedAssistantMessageRef.current = false;
          }

          dispatch(setDisconnectedByInactivity(true));
          dispatch(setShowInactivityWarning(true));
          dispatch(setInactivityCountdown(0));

          // Pass keepDisconnectedByInactivity so handleVoiceModeStop does not clear
          // the disconnectedByInactivity flag we just set
          handleVoiceModeStop({ keepDisconnectedByInactivity: true });
        },
        onPauseStateChange: (isPaused: boolean) => {
          logger.log('[useVoiceModeHandlers] Pause state changed:', isPaused);
          dispatch(setVoiceModePaused(isPaused));
        },
      });
    } catch (error) {
      console.error('[useVoiceModeHandlers] Failed to start voice mode:', error);
      setVoiceModeLoading(false);
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      voiceSessionStartedRef.current = false;

      const errorMessage =
        error instanceof Error ? error.message : 'Unable to start voice mode. Please try again later.';
      setVoiceModeErrorMessage(errorMessage);
      setShowVoiceModeError(true);
    }
  };

  // ─── Stop voice mode ──────────────────────────────────────────────────────────
  const handleVoiceModeStop = async (options?: { keepDisconnectedByInactivity?: boolean }) => {
    logger.log('[VOICE MODE] Stopping voice mode and switching to regular chat');

    dispatch(cancelTranscribingMessages());

    await voiceModeService.disconnect();
    dispatch(setVoiceModeActive(false));
    dispatch(setVoiceModeListening(false));
    dispatch(setVoiceModePaused(false));
    dispatch(setVoiceModeRestarting(false));
    dispatch(setShowInactivityWarning(false));
    dispatch(setInactivityCountdown(0));
    if (!options?.keepDisconnectedByInactivity) {
      dispatch(setDisconnectedByInactivity(false));
    }
    setAudioLevel(0);
    setVoiceModeLoading(false);
    voiceSessionStartedRef.current = false;

    hasCreatedAssistantMessageRef.current = false;
    hasActiveVoiceUserMessageRef.current = false;
    assistantResponsePendingRef.current = false;
    voiceModeCancelledRef.current = false;
  };

  // ─── Dismiss inactivity warning / reconnect ────────────────────────────────
  const handleDismissInactivityWarning = async () => {
    logger.log('[useVoiceModeHandlers] Dismissing inactivity warning and resuming voice mode');
    dispatch(setShowInactivityWarning(false));
    dispatch(setDisconnectedByInactivity(false));

    if (disconnectedByInactivity) {
      try {
        dispatch(setVoiceModeRestarting(true));
        await voiceModeService.disconnect();
        await startVoiceMode();
        dispatch(setVoiceModeRestarting(false));
      } catch (error) {
        logger.error('[useVoiceModeHandlers] Failed to restart voice mode after dismissing warning:', error);
        dispatch(setVoiceModeRestarting(false));
        setVoiceModeErrorMessage('Failed to restart voice mode. Please try again.');
      }
    }
  };

  // ─── Pause / resume microphone ────────────────────────────────────────────
  const handleVoiceModePauseToggle = async () => {
    if (isVoiceModePaused) {
      dispatch(setShowInactivityWarning(false));

      try {
        dispatch(setVoiceModeRestarting(true));
        await voiceModeService.resumeAudioCapture();
        dispatch(setVoiceModePaused(false));
        dispatch(setVoiceModeRestarting(false));
      } catch (error) {
        console.error('[VOICE MODE] Failed to resume microphone:', error);
        dispatch(setVoiceModeRestarting(false));
        await handleVoiceModeStop();
      }
    } else {
      try {
        await voiceModeService.pauseAudioCapture();
        dispatch(setVoiceModePaused(true));
      } catch (error) {
        console.error('[VOICE MODE] Failed to pause microphone:', error);
      }
    }
  };

  // ─── Voice mode button click (handles cancel / stop / start) ─────────────
  const handleVoiceModeButtonClick = async () => {
    if (voiceModeLoading) {
      logger.log('[useVoiceModeHandlers] Canceling voice mode initialization');
      voiceModeCancelledRef.current = true;
      setVoiceModeLoading(false);
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      hasCreatedAssistantMessageRef.current = false;
      hasActiveVoiceUserMessageRef.current = false;
      assistantResponsePendingRef.current = false;
      await voiceModeService.disconnect();
      return;
    }

    if (isVoiceModeActive) {
      logger.log('[useVoiceModeHandlers] Ending voice mode');
      voiceModeCancelledRef.current = false;
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      hasCreatedAssistantMessageRef.current = false;
      hasActiveVoiceUserMessageRef.current = false;
      assistantResponsePendingRef.current = false;
      await voiceModeService.disconnect();
      return;
    }

    logger.log('[useVoiceModeHandlers] Voice mode button clicked, starting initialization');

    const permissionState = await checkMicrophonePermission();
    logger.log('[useVoiceModeHandlers] Microphone permission state:', permissionState);

    if (permissionState === 'denied') {
      setShowMicPermissionDialog(true);
      return;
    }

    startVoiceMode();
  };

  // ─── Microphone selector dialog callback ─────────────────────────────────
  const handleMicrophoneSelected = async (deviceId: string) => {
    logger.log('[useVoiceModeHandlers] Microphone selected:', deviceId);
    setSelectedMicDeviceId(deviceId);
    try {
      localStorage.setItem('selectedMicrophoneId', deviceId);
    } catch (e) {
      console.warn('[useVoiceModeHandlers] Failed to save microphone preference:', e);
    }
    setShowMicSelector(false);

    if (isVoiceModeActive) {
      await voiceModeService.disconnect();
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      setAudioLevel(0);
      hasCreatedAssistantMessageRef.current = false;
      hasActiveVoiceUserMessageRef.current = false;
      assistantResponsePendingRef.current = false;

      setTimeout(() => {
        startVoiceMode();
      }, 100);
    }
  };

  // ─── No-audio-detected dialog handlers ───────────────────────────────────
  // Confirm = user wants to switch microphone; dismiss = they want to continue anyway
  const handleNoAudioDialogConfirm = () => {
    dispatch(setShowNoAudioDialog(false));
    setShowMicSelector(true);
  };

  const handleNoAudioDialogDismiss = () => {
    dispatch(setShowNoAudioDialog(false));
  };

  // ─── Return ───────────────────────────────────────────────────────────────
  return {
    // State values needed by JSX
    voiceModeLoading,
    audioLevel,
    frequencyData,
    showMicPermissionDialog,
    setShowMicPermissionDialog,
    showVoiceModeError,
    setShowVoiceModeError,
    voiceModeErrorMessage,
    showMicSelector,
    setShowMicSelector,
    // Handlers
    startVoiceMode,
    handleVoiceModeButtonClick,
    handleDismissInactivityWarning,
    handleVoiceModePauseToggle,
    handleVoiceModeStop,
    handleMicrophoneSelected,
    handleNoAudioDialogConfirm,
    handleNoAudioDialogDismiss,
  };
}
